import { db, auth } from "./firebase"; // ✅ path is correct if you're in src/
import { collection, addDoc, serverTimestamp, doc, setDoc, Timestamp, getDoc } from "firebase/firestore";
import React, { useState, useEffect } from "react";
import Select from "react-select";
import Papa from "papaparse";
import AsyncSelect from "react-select/async";
import { fetchLogStats } from "./utils";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import styles from './LogsTab.module.css';




function ExamSkills({ selectedSkills, setSelectedSkills }) {
  const sections = [
    {
      title: "Preliminary Testing & History",
      skills: ["Chief Complaint (CC) & History of Present Illness (HPI)", "Medical, Ocular, and Family History", "Visual Acuity", "Pupillary Testing", "Extraocular Motilities (EOMs)", "Cover Test", "Confrontation Visual Fields", "Color Vision & Stereopsis", "Lensometry", "Autorefraction / Keratometry", "Intraocular Pressure (Tonometry)"],
    },
    {
      title: "Refraction",
      skills: ["Retinoscopy", "Autorefraction", "Subjective Refraction", "Binocular Balance"],
    },
    {
      title: "Ocular Health Evaluation",
      skills: ["External Exam (lids, lashes, adnexa)", "Slit Lamp Exam (cornea, anterior chamber, iris, lens)", "Fundus Exam (optic nerve, retina, macula)—often done with dilation", "Gonioscopy (if glaucoma suspected)"],
    },
    {
      title: "Special Testing",
      skills: ["Optical Coherence Tomography (OCT)", "Visual Fields", "Tear Film Testing (TBUT, Schirmer’s)", "Corneal Topography / Pachymetry", "Fundus Photography", "Additional binocular vision or accommodative tests", "Others as needed based on findings"],
    },
    {
      title: "Assessment, Plan & Patient Education",
      skills: ["Final Diagnosis and Management Plan", "Prescribe glasses/contact lenses", "Recommend medications or therapy", "Patient education (ocular health, treatment plan, follow-up)", "Referral to a specialist if needed"],
    },
  ];

  const uncommonSkills = {
  "Preliminary Testing & History": [
    "Near Point of Convergence (NPC)",
    "Vergence Ranges (Base-in/Base-out ranges)",
    "Accommodative Amplitude, Facility, and Lag testing",
    "Blood Pressure measurement",
    "Pulse Oximetry",
    "Amsler Grid (macular function screening)",
    "Red Cap Desaturation (optic nerve function)",
    "Maddox Rod / Maddox Wing tests",
    "Heterophoria Testing (von Graefe, Howell Card)",
    "Fixation Disparity Testing (Wesson Card, Saladin Card)",
    "Dark Adaptation Screening (basic handheld tests)",
    "Questionnaires: OSDI, CISS, NEI VFQ-25",
  ],
  "Refraction": [
    "Cycloplegic Refraction",
    "Near Refraction",
    "Refraction for Irregular Corneas",
    "Stenopaic Slit Refraction",
    "Radical Retinoscopy",
    "Over-plus and Fogging Techniques",
    "Duochrome Test",
    "MEM Retinoscopy",
  ],
  "Ocular Health Evaluation": [
    "Corneal Staining",
    "Tear Break-Up Time (TBUT)",
    "Schirmer’s Test",
    "Lid Eversion & Meibomian Gland Expression",
    "Seidel Test",
    "Corneal Sensitivity Testing",
    "Specular Microscopy",
    "Scleral Depression",
    "Anterior Segment Photography",
    "Lid Margin and Lash Debris Evaluation",
  ],
  "Special Testing (If Indicated)": [
    "B-scan Ultrasonography",
    "Electrodiagnostics (ERG, VEP, EOG)",
    "Anterior Segment OCT",
    "Fundus Autofluorescence",
    "Confocal Microscopy",
    "Tear Film Interferometry",
    "Tear Osmolarity Testing",
    "InflammaDry MMP-9 Testing",
    "Macular Pigment Optical Density Testing",
    "Microperimetry",
    "Multifocal ERG / Pattern ERG",
    "Contrast Sensitivity Testing",
    "Dark Adaptometry",
  ],
  "Assessment, Plan & Patient Education": [
    "Specialty Contact Lens Fitting",
    "Myopia Control Treatments",
    "Low Vision Rehabilitation",
    "Vision Therapy Prescription",
    "Sports Vision Training",
    "Nutritional Counseling",
    "Safety & Occupational Lens Counseling",
    "Driving Restriction Counseling",
    "Minor In-Office Procedures",
    "Minor Laceration Repair",
  ],
};


  const toggleSkill = (skill) => {
    setSelectedSkills((prev) =>
      prev.includes(skill)
        ? prev.filter((s) => s !== skill)
        : [...prev, skill]
    );
  };

  return (
    <div className={styles.formGroup}>
      <h3 style={{ color: "#115680", marginBottom: "10px" }}>Skills Performed</h3>
      {sections.map((section) => (
        <div key={section.title} style={{ marginBottom: "16px", borderRadius: "12px", backgroundColor: "#f9fbfd", boxShadow: "0 1px 4px rgba(0,0,0,0.1)" }}>
          <details style={{ padding: "12px 16px", borderRadius: "12px", cursor: "pointer" }}>
            <summary style={{ fontWeight: "600", fontSize: "16px", color: "#20466b" }}>{section.title}</summary>
            <div style={{ marginTop: "10px", paddingLeft: "10px" }}>
              {section.skills.map((skill) => (
                <label key={skill} style={{ display: "flex", alignItems: "center", marginBottom: "8px", fontSize: "14px", color: "#333" }}>
                  <input
                    type="checkbox"
                    checked={selectedSkills.includes(skill)}
                    onChange={() => toggleSkill(skill)}
                    style={{ marginRight: "8px" }}
                  />
                  {skill}
                </label>
              ))}

              <Select
  options={(uncommonSkills[section.title] || []).map(skill => ({ value: skill, label: skill }))}
  placeholder={`Search uncommon ${section.title} skills...`}
  isClearable
  isMulti
  onChange={(selectedOptions) => {
    const selectedValues = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
    selectedValues.forEach((skill) => {
      if (!selectedSkills.includes(skill)) toggleSkill(skill);
    });
  }}
  styles={{ container: base => ({ ...base, marginTop: "8px" }) }}
/>

            </div>
          </details>
        </div>
      ))}
    </div>
  );
}

function LogsTab({ refreshStats }) {
  const [site, setSite] = useState("");
  const [icd10Options, setIcd10Options] = useState([]);
  const [selectedCptCodes, setSelectedCptCodes] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [date, setDate] = useState(() => {
  const today = new Date();
  return today.toISOString().split("T")[0];  // default to today's date in yyyy-mm-dd format
});
  const [customLocation, setCustomLocation] = useState("");
  const [involvement, setInvolvement] = useState("Patient Encounter - Exercised independent judgment");
  const performedWithOthers = involvement === "Patient Exposure - Screening, Testing, Shadowing";
  const [serviceType, setServiceType] = useState("Primary Care");
  const [ageGroup, setAgeGroup] = useState("0-18");
  const [gender, setGender] = useState("Male");
  const [race, setRace] = useState("Asian");
  const [duration, setDuration] = useState(15);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [selectedVisitType, setSelectedVisitType] = useState("");
  const [selectedServiceType, setSelectedServiceType] = useState("");
  const [selectedDiagnoses, setSelectedDiagnoses] = useState([]);
  const [submitMultiple, setSubmitMultiple] = useState(false);
  const [numberOfCopies, setNumberOfCopies] = useState(1);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

const handleSaveLog = () => {
  setShowConfirmModal(true);
};

const confirmAndSaveLog = async () => {
  setShowConfirmModal(false); // close the confirmation modal

  try {
    const userEmail = auth.currentUser?.email;
    if (!userEmail) {
      alert("No user is logged in.");
      return;
    }

    const userRef = doc(db, "users", userEmail);
    const userSnap = await getDoc(userRef);
    const userData = userSnap.data();

    const formattedDate = new Date(date).toISOString().split("T")[0];
    const cptCodes = selectedCptCodes.map((item) => item.value);

    const logData = {
      academicYear,
      involvement,
      date: formattedDate,
      serviceSite: customLocation || site,
      serviceType,
      visitType: selectedVisitType,
      diagnoses: selectedDiagnoses,
      skills: selectedSkills,
      ageGroup,
      gender,
      race,
      duration: Number(duration),
      cptCodes,
      timestamp: new Date().toISOString(),
      cohort: userData?.cohort || "Unknown", // ✅ pulled from user profile
      email: userEmail                          // ✅ required for /logs write rule
    };

    for (let i = 0; i < numberOfCopies; i++) {
      await addDoc(collection(db, "users", userEmail, "logs"), logData);
      await addDoc(collection(db, "logs"), logData);
    }

    if (refreshStats) {
      await refreshStats();
    }

    setShowSuccessModal(true);
  } catch (error) {
    console.error("Error saving log:", error);
    alert("Error saving log. Check console for details.");
  }
};





const clearForm = () => {
  setAcademicYear("");
  setInvolvement("");
  setDate(""); // or new Date() if you want today's date again
  setSite("");
  setCustomLocation("");
  setServiceType("");
  setSelectedVisitType("");
  setSelectedDiagnoses([]);
  setSelectedSkills([]);
  setAgeGroup("");
  setGender("");
  setRace("");
  setDuration("");
  setSelectedCptCodes([]);
  setNumberOfCopies(1);
};

  
  const serviceSites = [
    "Campus Clinic", "Mobile Clinic", "Community Health Fair", "Community Screening",
    "Private Practice Observation", "Veterans Affairs Clinic", "School Screening",
    "Homeless Shelter Clinic", "Low Vision Rehabilitation Clinic", "Pediatric Specialty Clinic",
    "Hospital-Based Optometry", "Indigenous Health Clinic", "Telehealth Visit",
    "Geriatric Facility Clinic", "Remote Learning Activity", "Local Elementary School",
    "Local Middle School", "Local High School", "School Nurse Office",
    "Occupational Health Setting", "Public Health Department", "Vision Therapy Center",
    "Multidisciplinary Clinic", "On-Campus Simulation Lab", "Inpatient Hospital Unit",
    "Emergency Department", "Urgent Care", "Oncology Clinic", "Diabetes Specialty Clinic",
    "Neuro-Optometry Clinic", "Sports Vision Clinic", "Contact Lens Specialty Clinic",
    "Retina Specialty Clinic", "Cornea Specialty Clinic", "Glaucoma Specialty Clinic",
    "Comprehensive Eye Care Center", "Women's Shelter Clinic", "Pediatric Hospital Ward",
    "Infectious Disease Clinic", "Rural Outreach Program", "International Mission Trip",
    "Research Study Encounter", "Student-Led Health Initiative", "Other"
  ];

  const cptOptions = [
    "92002 - Intermediate Examination - NP", "92004 - Comprehensive Examination - NP",
    "99201 - Evaluation & Manage Level 1", "99202 - Evaluation & Manage Level 2",
    "99203 - Evaluation & Manage Level 3", "99204 - Evaluation & Manage Level 4",
    "99205 - Evaluation & Manage Level 5", "S0620 - Routine Ophth Ex w/Refr",
    "92012 - Intermediate Examination - EP", "92014 - Comprehensive Examination - EP",
    "99211 - Evaluation & Manage Level 1", "99212 - Evaluation & Manage Level 2",
    "99213 - Evaluation & Manage Level 3", "99214 - Evaluation & Manage Level 4",
    "99215 - Evaluation & Manage Level 5", "S0621 - Routine Ophth Ex w/Refr",
    "90027 - No Charge Office Visit", "NC - Completion Of Exam No Charge",
    "92015 - Refraction", "CL Follow Up No Charge",
    "92310 - CL Evaluation - Level 1 (Spherical Soft, Toric Soft)",
    "92310 - CL Evaluation - Level 2 (Rigid Gas Permeable, Multifocal, Monovision)",
    "92310 - CL Services (+F/U) Level 1", "92310 - CL Services (+F/U) Level 2",
    "92310 - CL Services (I/R + F/U) Level 1", "92310 - CL Services (I/R + F/U) Level 2",
    "92310 - CL Fit 4 (Keratoconic) - EP", "92310 - CL Fit 4 (Keratoconic) - NP",
    "92311 - CL Fitting For Aphakia - 1 Eye", "92312 - CL Fitting For Aphakia - Both Eyes",
    "92313 - CL Fitting - Corneoscleral Lens", "92325 - Modification Of CL",
    "92326 - Replacement Of CL", "99241 - Initial Consult Level 1", "99242 - Initial Consult Level 2",
    "99243 - Initial Consult Level 3", "99244 - Initial Consult Level 4", "99245 - Initial Consult Level 5",
    "99341 - Home Visit (Prob Focus) -NP", "99342 - Home Visit (Exp Prob Focus) -NP",
    "99343 - Home Visit (Detailed) -NP", "99344 - Home Visit (Comp W/ModCmplx)-NP",
    "99345 - Home Visit (Comp W/Hi Cmplx)-NP", "99347 - Home Visit(Prob Focus) -EP",
    "99348 - Home Visit(Exp Prob Focus) -EP", "99349 - Home Visit(Detailed) -EP",
    "99350 - Home Visit(Comprehensive) -EP", "99354 - Prolonged OV Svc 1st Hour",
    "99355 - Prolonged OV Svc Each Addl 30", "66984 - Post Op Cataract -RT",
    "6698L - Post Op Cataract -LT", "66821 - Post Op YAG Laser -RT", "6682L - Post Op YAG Laser -LT",
    "66761 - Post Op LPI (10 Days) -RT", "6676L - Post Op LPI (10 Days) -LT",
    "66999 - Post Op Lasik -RT", "6699L - Post Op Lasik -LT", "99024 - Post-Operative Care - N/C",
    "65855 - Post Op Selective Laser Trabeculoplasty", "687RL - Punctal Closure-Plug RT Lower",
    "687RU - Punctal Closure-Plug RT Upper", "687LL - Punctal Closure-Plug LT Lower",
    "687LU - Punctal Closure-Plug LT Upper", "68801 - Dilate Puncta w/ or w/o Irrig",
    "92020 - Gonioscopy", "92100 - Serial Tonometry", "92145 - Corneal Hysteresis",
    "92060 - Sensorimotor Exam", "92065 - In-Office Vision Therapy", "92070 - Fit & Supply Of Bandage CL",
    "92225 - Extended Ophthalmoscopy-Init", "92226 - Extended Ophthalmoscopy-Subseq",
    "76514 - Corneal Pachymetry", "92081 - VF Limited Gldmn 1 Isopter", "92082 - VF Intermediate",
    "92083 - VF Extended", "92250 - Fundus Photography", "92285 - External Photography",
    "92025 - Corneal Topography", "92132 - OCT Imaging - Anterior Segment",
    "92133 - OCT Imaging - Optic Nerve", "92134 - OCT Imaging - Retina", "76512 - Ultrasound B-Scan",
    "92230 - Fluorescein Angiography", "92283 - Color Vision Extended",
    "92270 - Electro-Oculogram", "92275 - Electroretinography", "95930 - Visually Evoked Potential",
    "92286 - Endothelial Cell Analysis", "Cataract Extraction", "Refractive Surgery",
    "Retinal Procedures", "Oculoplastic Procedures", "Strabismus Procedures",
    "Corneal Procedures", "99070 - Medical Supplies & Materials", "92062 - Parent/Patient Conference",
    "92063 - Visual Inform Processing-New", "92064 - Visual Inform Processing-Est",
    "92284 - Dark adaptation examination", "VTCHK - VT Progress Check",
    "RSPAT - Research/Study Visit", "67820 - Epilation",
    "65222 - Removal of foreign body, corneal, with slit lamp"
  ];

  // === Load ICD10 CSV ===
  useEffect(() => {
  Papa.parse(process.env.PUBLIC_URL + "/icd10_codes.csv", {
    download: true,
    header: true,
    skipEmptyLines: true,
    complete: (results) => {
      console.log("Parsed ICD-10 CSV:", results.data);
      
      const codes = results.data
        .map(row => row.Combined)
        .filter(code => !!code);  // filter out any undefined/null/empty
      
      console.log("Extracted Codes:", codes);
      setIcd10Options(codes);
    },
    error: (error) => {
      console.error("Error loading ICD-10 CSV:", error);
    },
  });
}, []);


  // === Search Handler for ICD10 ===
  const loadIcd10Options = (inputValue, callback) => {
  const filtered = icd10Options
    .filter(code => code.toLowerCase().includes(inputValue.toLowerCase()))
    .slice(0, 100)  // Limit to first 100 matches to avoid lag
    .map(code => ({ value: code, label: code }));

  callback(filtered);
};

  return (
  <div className={styles.content}>

    <div className={styles.formGroup}>
      <label>Academic Year</label>
      <Select
  options={[
    { value: "1st and 2nd year log", label: "1st and 2nd year log" },
    { value: "3rd year log", label: "3rd year log" },
    { value: "4th year log", label: "4th year log" },
  ]}
  placeholder="Select Academic Year..."
  onChange={(selected) => setAcademicYear(selected ? selected.value : "")}
  isClearable
  value={academicYear ? { value: academicYear, label: academicYear } : null}
/>

    </div>

    <div className={styles.formGroup}>
  <label>Involvement</label>
  <div>
    <label>
      <input
        type="radio"
        value="Patient Encounter - Exercised independent judgment"
        checked={involvement === "Patient Encounter - Exercised independent judgment"}
        onChange={(e) => setInvolvement(e.target.value)}
      />
      Patient Encounter - Exercised independent judgment
    </label>
  </div>
  <div>
    <label>
      <input
        type="radio"
        value="Patient Exposure - Screening, Testing, Shadowing"
        checked={involvement === "Patient Exposure - Screening, Testing, Shadowing"}
        onChange={(e) => setInvolvement(e.target.value)}
      />
      Patient Exposure - Screening, Testing, Shadowing
    </label>
  </div>
</div>


    <div className={styles.formGroup}>
  <label>Date</label>
  <DatePicker
    selected={date ? new Date(date) : null}
    onChange={(date) => setDate(date ? date.toISOString().split("T")[0] : "")}
    dateFormat="MM/dd/yyyy"
    placeholderText="Select a date"
    className="form-control"
    showPopperArrow={false}
    todayButton="Today"
    isClearable
  />
</div>

    <div className={styles.formGroup}>
      <label>Service Site</label>
      <Select
    options={serviceSites.map((site) => ({ value: site, label: site }))}
    onChange={(selected) => setSite(selected ? selected.value : "")}
    value={site ? { value: site, label: site } : null}
    placeholder="Select service site..."
    isClearable
  />
</div>
    {site === "Other" && (
  <div className={styles.formGroup}>
    <label>Custom Service Site</label>
    <input
      type="text"
      value={customLocation}
      onChange={(e) => setCustomLocation(e.target.value)}
      placeholder="Enter custom location"
    />
  </div>
)}

<div className={styles.formGroup}>
  <label>Service Type</label>
  <Select
    options={[
      { value: "Primary Care", label: "Primary Care" },
      { value: "Pediatrics", label: "Pediatrics" },
      { value: "Low Vision/Geriatrics", label: "Low Vision/Geriatrics" },
      { value: "Systemic Disease", label: "Systemic Disease" },
      { value: "Remote Learning Activity", label: "Remote Learning Activity" },
    ]}
    onChange={(selected) => setServiceType(selected ? selected.value : "")}
    value={serviceType ? { value: serviceType, label: serviceType } : null}
    placeholder="Select service type..."
    isClearable
  />
</div>

<div className={styles.formGroup}>
  <label>Age Group</label>
  <Select
  options={[
    { value: "0-18", label: "0-18" },
    { value: "19-69", label: "19-69" },
    { value: "70+", label: "70+" },
  ]}
  value={
    ageGroup
      ? { value: ageGroup, label: ageGroup }
      : null
  }
  onChange={(selected) =>
    setAgeGroup(selected ? selected.value : "")
  }
  placeholder="Select age group..."
  isClearable
/>
</div>

<div className={styles.formGroup}>
  <label>Gender</label>
  <div>
    <label>
      <input
        type="radio"
        value="Male"
        checked={gender === "Male"}
        onChange={(e) => setGender(e.target.value)}
      />
      Male
    </label>
  </div>
  <div>
    <label>
      <input
        type="radio"
        value="Female"
        checked={gender === "Female"}
        onChange={(e) => setGender(e.target.value)}
      />
      Female
    </label>
  </div>
</div>

<div className={styles.formGroup}>
  <label>Race/Ethnicity</label>
  <Select
  options={[
    { value: "Asian", label: "Asian" },
    { value: "Black/African American", label: "Black/African American" },
    { value: "Hispanic/Latino", label: "Hispanic/Latino" },
    { value: "Native American/Alaskan Native", label: "Native American/Alaskan Native" },
    { value: "Pacific Islander", label: "Pacific Islander" },
    { value: "White", label: "White" },
    { value: "Other/NA", label: "Other/NA" },
  ]}
  value={race ? { value: race, label: race } : null}
  onChange={(selected) => setRace(selected ? selected.value : "")}
  placeholder="Select race/ethnicity..."
  isClearable
/>
</div>

<div className={styles.formGroup}>
  <label>Time Spent with Patient (minutes)</label>
  <input
    type="number"
    min="1"
    value={duration}
    onChange={(e) => setDuration(e.target.value)}
    placeholder="Minutes"
  />
</div>

<div className={styles.formGroup}>
  <label>Visit Type</label>
  <select
    value={selectedVisitType}
    onChange={(e) => setSelectedVisitType(e.target.value)}
    style={{
      padding: "8px",
      fontSize: "14px",
      borderRadius: "4px",
      width: "100%",
      marginTop: "6px",
    }}
  >
    <option value="">-- Select Visit Type --</option>
  <option value="Comprehensive Exam">Comprehensive Exam</option>
  <option value="Follow-Up">Follow-Up</option>
  <option value="Emergency">Emergency</option>
  <option value="Contact Lens Evaluation">Contact Lens Evaluation</option>
  <option value="Specialty Contact Lens">Specialty Contact Lens</option>
  <option value="Dry Eye Evaluation">Dry Eye Evaluation</option>
  <option value="LASIK Post-Op">LASIK Post-Op</option>
  <option value="Cataract Co-Management">Cataract Co-Management</option>
  </select>
</div>

<ExamSkills
  selectedSkills={selectedSkills}
  setSelectedSkills={setSelectedSkills}
/>

      <div className={styles.formGroup}>
  <label>CPT Codes</label>
  <Select
    options={cptOptions.map(code => ({ value: code, label: code }))}
    value={selectedCptCodes} // ✅ current selection
    onChange={(selected) => setSelectedCptCodes(selected || [])} // ✅ update on change
    placeholder="Type to search CPT code..."
    isClearable
    isMulti
  />
</div>

      <div className={styles.formGroup}>
  <label>Diagnosis (ICD10)</label>
<AsyncSelect
  cacheOptions
  loadOptions={loadIcd10Options}
  defaultOptions={false}
  placeholder="Type to search ICD10..."
  isClearable
  isMulti
  value={
    selectedDiagnoses.map((code) => ({
      value: code,
      label: code,
    }))
  }
  noOptionsMessage={() => null}
  onChange={(selectedOptions) => {
    const selectedValues = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
    setSelectedDiagnoses(selectedValues);
  }}
/>
</div>

<div style={{ marginTop: "16px" }}>
  <label>
    <input
      type="checkbox"
      checked={submitMultiple}
      onChange={(e) => {
        setSubmitMultiple(e.target.checked);
        if (!e.target.checked) setNumberOfCopies(1); // Reset
      }}
      style={{ marginRight: "8px" }}
    />
    Submit multiple identical logs
  </label>

  {submitMultiple && (
    <div style={{ marginTop: "8px" }}>
      <label htmlFor="numberOfCopies" style={{ fontWeight: "bold", display: "block" }}>
        Number of copies:
      </label>
      <input
        type="number"
        id="numberOfCopies"
        min="1"
        max="20"
        value={numberOfCopies}
        onChange={(e) => setNumberOfCopies(Math.max(1, parseInt(e.target.value) || 1))}
        style={{ padding: "6px", width: "80px" }}
      />
    </div>
  )}
</div>

<div className={styles.formGroup} style={{ marginTop: "20px", textAlign: "center" }}>
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      gap: "12px",
      marginTop: "20px",
    }}
  >
    <button
      onClick={handleSaveLog}
      style={{
        padding: "10px 20px",
        backgroundColor: "#d9ebf5", // cream blue
        color: "#115680",           // deep blue
        border: "none",
        borderRadius: "8px",
        fontSize: "16px",
        cursor: "pointer",
        fontWeight: "bold",
      }}
    >
      Save Log
    </button>

    <button
      onClick={clearForm}
      style={{
        padding: "10px 20px",
        backgroundColor: "#ff4d4d", // bright red
        color: "#ffffff",           // white text
        border: "none",
        borderRadius: "8px",
        fontSize: "16px",
        cursor: "pointer",
        fontWeight: "bold",
      }}
    >
      Clear Form
    </button>
  </div>
</div>



    {showConfirmModal && (
  <div className={styles.modalOverlay}>
    <div className={styles.modalBox}>
      <p>Are you sure you want to save this log?</p>
      <div style={{ marginTop: "20px" }}>
        <button className={styles.modalButton} onClick={confirmAndSaveLog}>
          Yes, Save
        </button>
        <button className={styles.modalButton} onClick={() => setShowConfirmModal(false)}>
          Cancel
        </button>
      </div>
    </div>
  </div>
)}

{showSuccessModal && (
  <div className={styles.modalOverlay}>
    <div className={styles.modalBox}>
      <p>
        {numberOfCopies} log{numberOfCopies > 1 ? "s" : ""} saved successfully!
      </p>
      <button className={styles.modalButton} onClick={() => setShowSuccessModal(false)}>
        OK
      </button>
    </div>
  </div>
)}

</div>
  );
}

export default LogsTab;

